import { useState, useEffect, useCallback } from 'react'
import { X, StickyNote } from 'lucide-react'

interface AddNoteModalProps {
  isOpen: boolean
  onClose: () => void
  currentNotes?: string
  onSave: (notes: string) => void
  isPending?: boolean
}

export function AddNoteModal({
  isOpen,
  onClose,
  currentNotes,
  onSave,
  isPending = false,
}: AddNoteModalProps) {
  // Use key-based remount strategy - modal content is only rendered when open
  // This ensures fresh state on each open
  if (!isOpen) return null

  return (
    <AddNoteModalContent
      onClose={onClose}
      currentNotes={currentNotes}
      onSave={onSave}
      isPending={isPending}
    />
  )
}

interface AddNoteModalContentProps {
  onClose: () => void
  currentNotes?: string
  onSave: (notes: string) => void
  isPending: boolean
}

function AddNoteModalContent({
  onClose,
  currentNotes,
  onSave,
  isPending,
}: AddNoteModalContentProps) {
  // Initialize with current notes for editing
  const [note, setNote] = useState(currentNotes || '')
  const isEditing = !!currentNotes

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    [onClose]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Save the notes (can be empty to clear notes)
    onSave(note.trim())
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-note-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2
            id="add-note-modal-title"
            className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 flex items-center gap-2"
          >
            <StickyNote className="w-5 h-5 text-[#6369D1]" />
            {isEditing ? 'Edit Notes' : 'Add Note'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Write a note about this guest..."
              rows={4}
              disabled={isPending}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 resize-none"
              autoFocus
            />
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              Notes are private and only visible to your team.
            </p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-[#6369D1] hover:bg-[#6369D1]/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
